import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { getIssuedFormLink } from '@drivingdoc/api-client/generated/sdk';
import { StudentIssuedForm } from 'types/students';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface PropTypes {
  form: StudentIssuedForm;
}

function FormCard({ form }: PropTypes): JSX.Element {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'), { defaultMatches: true });
  const dialogs = useDialogs();
  dayjs.extend(advancedFormat);
  dayjs.extend(duration);
  dayjs.extend(relativeTime);

  // determine if the form is expired
  const formExpired = !!form.expires && dayjs(form.expires).isBefore(dayjs());

  const handleDownloadForm = async () => {
    try {
      const result = await getIssuedFormLink({
        path: {
          id: form.id,
        },
      });
      const url = (result.data as { url: string } | undefined)?.url;
      if (!url) {
        return;
      }
      
      // Fetch the PDF as a blob to trigger download
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Extract filename from the URL's response-content-disposition parameter if available
      const urlObj = new URL(url);
      const disposition = urlObj.searchParams.get('response-content-disposition');
      const filenameMatch = disposition?.match(/filename\*?=['"]?(?:UTF-8'')?([^'";\n]+)['"]?/);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'form.pdf';
      
      // Create download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error Downloading Form',
        message: getApiErrorMessage(error),
      });
    }
  };

  const handleOpenForm = async () => {
    // open a blank tab immediately on click
    const newTab = window.open('about:blank', '_blank');

    // check if the new tab was blocked
    if (!newTab) {
      alert('Popup was blocked. Please allow popups for this site.');
      return;
    }

    try {
      // get a presigned S3 link to the form
      const result = await getIssuedFormLink({
        path: {
          id: form.id,
        },
      });
      const url = (result.data as { url: string } | undefined)?.url;

      // ensure the URL was returned
      if (!url) {
        // close the tab if the URL is not valid
        newTab.close();
        throw new Error('Invalid URL');
      }

      // navigate the new tab
      newTab.location.href = url;
    } catch (error) {
      // close the tab if the request failed
      newTab.close();

      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Paper
      sx={{
        p: 1,
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#1A2027' : '#fff'),
      }}
    >
      <Grid container direction="row">
        {/* Left Column */}
        <Grid size={{ xs: 12, sm: 6 }} container direction="column">
          <Grid pb={1}>
            <Typography variant="subtitle1" lineHeight={1.25} fontWeight="bold">
              {form.form?.name}
            </Typography>
          </Grid>
        </Grid>

        {/* Right Column */}
        <Grid
          size={{ xs: 12, sm: 6 }}
          container
          direction="column"
          justifyContent="space-between"
          alignItems={{ xs: undefined, sm: 'flex-end' }}
          flexWrap="nowrap"
        >
          {/* <Grid pb={1}>
                        {form.expires && (
                            <Typography
                                variant="subtitle2"
                                lineHeight={1.25}
                                align={isXs ? 'center' : 'right'}
                                color={formExpired ? 'error.main' : 'text.secondary'}
                                fontWeight="bold"
                                pt={0.5}
                            >
                                {`Form expire${formExpired ? 'd' : 's'} ${dayjs
                                    .duration(dayjs(form.expires).diff())
                                    .humanize(true)}`}
                            </Typography>
                        )}
                    </Grid> */}
          <Grid pb={1}>
            <Typography
              variant="subtitle2"
              lineHeight={1.25}
              height={(theme) => `calc(${theme.typography.body1.fontSize} * 1.25)`}
              align={isXs ? 'center' : 'right'}
              color={formExpired ? 'error.main' : 'text.secondary'}
              fontWeight="bold"
              pt={0.5}
            >
              {form.expires &&
                `Form expire${formExpired ? 'd' : 's'} ${dayjs.duration(dayjs(form.expires).diff()).humanize(true)}`}
            </Typography>
          </Grid>

          <Grid>
            <ButtonGroup variant="outlined" sx={{ whiteSpace: 'nowrap' }} fullWidth={isXs} disabled={formExpired}>
              <Button onClick={handleDownloadForm} endIcon={<DownloadIcon />} sx={{ py: 0, mt: 1 }}>
                Download
              </Button>
              <Button onClick={handleOpenForm} endIcon={<OpenInNewIcon />} sx={{ py: 0, mt: 1 }}>
                Open In New Tab
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default FormCard;
